import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosClient from "../../api/api";
import {
  PREVIEW_WEIGHTS,
  HIGHLAND_CITIES,
  computeDeliveryPrice,
  currencySymbol,
  normaliseBrackets,
} from "../../data/deliveryTariffs";
import georgianCities from "../../data/georgianCities";

/**
 * Three-column price preview (Tbilisi / region / highland) for an office
 * that has a delivery_tariff set. Used by:
 *   - /admin/ab-office/:id  (admin sees what the current tariff produces)
 *
 * Fetches the office's currency rate from /api/currency on mount so that
 * per-kg numbers (in the office's foreign currency) render as GEL totals,
 * matching what PriceCalculator will bill. If the fetch fails we fall back
 * to a rate of 1 so numbers stay comparable but never render as zero.
 *
 * Props:
 *   office: { id, country, currency, delivery_tariff }
 *   compact?: drops the highland-cities legend when true
 */
const HomeDeliveryPriceTable = ({ office, compact = false }) => {
  const { t } = useTranslation();
  const tariff = office?.delivery_tariff;

  const [fxRate, setFxRate] = useState(null);

  useEffect(() => {
    // /api/currency is public — returns [{currency:"USD", toGel:2.62}, ...].
    // We only need the rate for the office's own currency; GEL is a no-op.
    const target = String(office?.currency || "USD").toUpperCase();
    if (target === "GEL") {
      setFxRate(1);
      return;
    }
    axiosClient
      .get("/currency")
      .then(({ data }) => {
        const hit = (data || []).find(
          (r) => String(r.currency).toUpperCase() === target
        );
        setFxRate(hit ? Number(hit.toGel) : 1);
      })
      .catch(() => setFxRate(1));
  }, [office?.currency]);

  // Nothing to render if the tariff hasn't been seeded yet — normaliseBrackets
  // handles both old and new shapes, so an empty result here really is empty.
  const brackets = normaliseBrackets(tariff);
  if (brackets.length === 0) return null;

  const highlandLabels = georgianCities
    .filter((c) => HIGHLAND_CITIES.has(c.value))
    .map((c) => c.label);

  const symbol = currencySymbol(office?.currency);

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-600 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800 flex justify-between items-baseline gap-4 flex-wrap">
        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
          {t("home_delivery_prices") || "სახლში მიტანის ტარიფი"}
        </p>
        <p className="text-xs text-slate-500">
          {t("prices_in_gel") || "ფასები ლარში (₾)"}
          {fxRate && fxRate !== 1 && (
            <>
              {" "}· 1 {symbol} = {Number(fxRate).toFixed(2)} ₾
            </>
          )}
        </p>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-white dark:bg-slate-700 text-slate-500 text-xs">
          <tr>
            <th className="px-4 py-2 text-left font-semibold">
              {t("weight") || "წონა"}
            </th>
            <th className="px-4 py-2 text-right font-semibold">
              {t("tbilisi") || "თბილისი"}
            </th>
            <th className="px-4 py-2 text-right font-semibold">
              {t("region") || "რეგიონი"}
            </th>
            <th className="px-4 py-2 text-right font-semibold">
              {t("highland") || "მაღალმთიანი"}
            </th>
          </tr>
        </thead>
        <tbody>
          {PREVIEW_WEIGHTS.map((w) => {
            const tb = computeDeliveryPrice(w, tariff, "tbilisi", fxRate);
            const rg = computeDeliveryPrice(w, tariff, "region", fxRate);
            const hl = computeDeliveryPrice(w, tariff, "highland", fxRate);
            return (
              <tr
                key={w}
                className="border-t border-slate-100 dark:border-slate-600
                           hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <td className="px-4 py-2 dark:text-slate-200">
                  {w} {t("admin_kg") || "კგ"}
                </td>
                <td className="px-4 py-2 text-right font-mono dark:text-white">
                  {tb.total_gel.toFixed(2)}
                  {tb.delivery_gel > 0 && (
                    <span className="text-[10px] text-slate-400 ml-1">
                      ({tb.transport_gel.toFixed(2)} + {tb.delivery_gel.toFixed(2)})
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono dark:text-white">
                  {rg.total_gel.toFixed(2)}
                  {rg.delivery_gel > 0 && (
                    <span className="text-[10px] text-slate-400 ml-1">
                      ({rg.transport_gel.toFixed(2)} + {rg.delivery_gel.toFixed(2)})
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono dark:text-white">
                  {hl.total_gel.toFixed(2)}
                  {hl.delivery_gel > 0 && (
                    <span className="text-[10px] text-slate-400 ml-1">
                      ({hl.transport_gel.toFixed(2)} + {hl.delivery_gel.toFixed(2)})
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {!compact && (
        <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50 dark:bg-slate-800">
          <b>{t("highland") || "მაღალმთიანი"}:</b> {highlandLabels.join(", ")}
        </div>
      )}
    </div>
  );
};

export default HomeDeliveryPriceTable;
